import {
  Article,
  Description,
  Download,
  FileUpload,
} from "@mui/icons-material";
import { IconButton, Stack, styled, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { usePatientHealthCareInformationContext } from "../../../contexts/PatientHealthCareInformationContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { GetBinary } from "../../../services/fhir/patientsummary/Binary/GetBinary";
import { IframeDialog } from "../../ehrrecords/components/IframeDialog";
import { ConfirmDocumentDialog } from "./ConfirmDocumentDialog";
import { isReactNativeWebView, selectFileRN } from "../../../utils/useWebViewBridge";

interface IProps {
  row: any;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export function ExamSolicitationActions({ row }: IProps) {
  const { user } = useSessionContext();
  const { setRefetchServiceRequest } = usePatientHealthCareInformationContext();
  const [openDocument, setOpenDocument] = useState<boolean>(false);
  const [viewResults, setViewResults] = useState<boolean>(false);
  const [base64, setBase64] = useState<any>();
  const [base64Results, setBase64Results] = useState<any>();
  const [fileBase64, setFileBase64] = useState<any>();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  // Detecta React Native de forma segura (com try-catch)
  const isRN = (() => {
    try {
      return isReactNativeWebView();
    } catch (e) {
      console.error('[ExamActions] Erro ao detectar RN:', e);
      return false;
    }
  })();

  async function getDocumentPdf() {
    const response = await GetBinary({ urlBinary: row?.binaryUrl });
    setBase64(response);
    return response;
  }

  async function downloadBase64File() {
    const auxDocument = !base64 ? await getDocumentPdf() : base64;
    const linkSource = `data:${auxDocument?.contentType};base64,${auxDocument?.data}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = "Solicitações de exames";
    downloadLink.click();
  }

  /**
   * MANIPULA CLIQUE NO BOTÃO DE UPLOAD
   * 
   * Detecta automaticamente o ambiente:
   * - Browser: Clica no input file escondido
   * - React Native: Abre seletor nativo de arquivos
   */
  const handleClickUpload = () => {
    console.log('[ExamActions] Click upload - isRN:', isRN);
    
    try {
      if (isRN) {
        // React Native: usa seletor nativo
        console.log('[ExamActions] Solicitando arquivo ao RN...');
        selectFileRN('.pdf', (result) => {
          console.log('[ExamActions] Arquivo recebido do RN:', result.fileName);
          setFileBase64(result.base64);
        });
      } else {
        // Browser: clica no input escondido
        console.log('[ExamActions] Browser - clicando no input file');
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    } catch (error) {
      console.error('[ExamActions] Erro no handleClickUpload:', error);
    }
  };

  /**
   * MANIPULA UPLOAD NO BROWSER (input file)
   * 
   * Esta função só é chamada no browser tradicional
   * quando o usuário seleciona arquivo via input
   */
  const uploadImage = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    
    console.log('[ExamActions] Arquivo selecionado (browser):', file.name);
    const base64 = await convertToBase64(file);
    setFileBase64(base64);
  };

  /**
   * CONVERTE ARQUIVO PARA BASE64 (browser)
   * 
   * No React Native, a conversão é feita nativamente
   */
  const convertToBase64 = (file: File) => {
    if (file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
          resolve(fileReader.result);
        };

        fileReader.onerror = (error) => {
          reject(error);
        };
      });
    }
  };

  const docReferenceData = {
    servicePratice: {
      code: row?.fullResource?.practiceSetting?.coding?.[0]?.code,
      system: row?.fullResource?.practiceSetting?.coding?.[0]?.system,
      display: row?.fullResource?.practiceSetting?.coding?.[0]?.display,
    },
    time: {
      start: dayjs().toISOString(),
      end: dayjs().toISOString(),
    },
  };

  const refs = {
    id: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Encounter"))
      ?.reference?.split("/")?.[1],
    subject: user?.username,
    appointmentId: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Appointment"))
      ?.reference?.split("/")?.[1],
  };

  return (
    <Stack width="fit-content">
      <Stack direction="row" spacing={1}>
        <IconButton onClick={() => setOpenDocument(true)}>
          <Tooltip title="Visualizar documento">
            <Article />
          </Tooltip>
        </IconButton>
        <IconButton onClick={() => downloadBase64File()}>
          <Tooltip title="Baixar">
            <Download />
          </Tooltip>
        </IconButton>

        {row.auxResults ? (
          <IconButton onClick={() => setViewResults(true)}>
            <Tooltip title="Resultado de exame">
              <Description />
            </Tooltip>
          </IconButton>
        ) : (
          <IconButton onClick={handleClickUpload}>
            <Tooltip title="Enviar resultado">
              <FileUpload sx={{ color: "primary700.main" }} />
            </Tooltip>
            {/* Input escondido - só renderiza no browser */}
            {!isRN && (
              <VisuallyHiddenInput 
                ref={fileInputRef} 
                type="file" 
                accept=".pdf"
                onChange={(e: any) => uploadImage(e)}
              />
            )}
          </IconButton>
        )}
      </Stack>

      {openDocument && (
        <IframeDialog
          handleClose={() => setOpenDocument(false)}
          isOpen={openDocument}
          data={row?.binaryUrl}
          title={row?.description}
          loadedBase64={base64}
          setLoaded={setBase64}
        />
      )}
      {viewResults && (
        <IframeDialog
          handleClose={() => setViewResults(false)}
          isOpen={viewResults}
          data={row?.auxResults?.resource?.content?.[0]?.attachment?.url || ""}
          title="Resultado de exame"
          loadedBase64={base64Results}
          setLoaded={setBase64Results}
        />
      )}

      {fileBase64 && (
        <ConfirmDocumentDialog
          setRenderButton={() => {}}
          setRefetch={setRefetchServiceRequest}
          fileBase64={fileBase64}
          isOpenDialog={fileBase64}
          onCloseDialog={() => {
            setFileBase64(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          basedOn={row?.fullResource?.basedOn?.filter((el: any) =>
            el.reference?.includes("ServiceRequest")
          )}
          encounterReference={refs}
          data={{
            ...docReferenceData,
            base64: fileBase64?.split(",")?.[1],
            codeValue: "RESULT",
            codeDisplay: "Resultado de exame",
            categoryValue: "resultado",
            categoryDisplay: "Resultado de exame",
          }}
        />
      )}
    </Stack>
  );
}
